
import React, { useEffect, useMemo, useState } from 'react';

import axios from 'axios';

import {
  CheckCircle,
  Loader2,
  XCircle,
  MapPin,
  User,
  Package,
  CreditCard,
  AlertCircle,
  Truck,
} from 'lucide-react';

import { useCart } from '../context/CartContext';

/* =========================================================
   API CONFIGURATION
========================================================= */

const API_BASE =
  process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') ||
  'https://apimyshop.myidealtd.co.uk';

/* =========================================================
   SHIPPING CONFIGURATION
========================================================= */

/*
  Fixed shipping cost.

  Every non-empty order will have £5.99 shipping.

  Example:

  Subtotal = £50.00
  Shipping = £5.99
  Total    = £55.99
*/

const SHIPPING_COST = 5.99;

/* =========================================================
   IMAGE URL HELPERS
========================================================= */

/**
 * Extract an actual image value from any supported structure.
 *
 * Supported:
 *
 * image: "product.jpg"
 * image: "/uploads/product.jpg"
 * image: "uploads/product.jpg"
 *
 * image: {
 *   original: "...",
 *   main: "...",
 *   thumbnail: "...",
 *   cart: "..."
 * }
 *
 * images: [...]
 *
 * product.image
 * product.images
 */

const extractImageValue = (item) => {
  if (!item) {
    return null;
  }

  const candidates = [
    item?.image,
    item?.photo,
    item?.product?.image,
    item?.images,
    item?.product?.images,
  ];

  for (const candidate of candidates) {
    const value = extractFromImageCandidate(candidate);

    if (value) {
      return value;
    }
  }

  return null;
};

/**
 * Extract image from string / array / object.
 */

const extractFromImageCandidate = (candidate) => {
  if (!candidate) {
    return null;
  }

  /* -------------------------------------------------------
     STRING
  ------------------------------------------------------- */

  if (typeof candidate === 'string') {
    const value = candidate.trim();

    return value || null;
  }

  /* -------------------------------------------------------
     ARRAY
  ------------------------------------------------------- */

  if (Array.isArray(candidate)) {
    if (candidate.length === 0) {
      return null;
    }

    for (const image of candidate) {
      const value = extractFromImageCandidate(image);

      if (value) {
        return value;
      }
    }

    return null;
  }

  /* -------------------------------------------------------
     IMAGE OBJECT
  ------------------------------------------------------- */

  if (
    typeof candidate === 'object' &&
    candidate !== null
  ) {
    /*
      Prefer the optimized cart image first.
    */

    const objectCandidates = [
      candidate.cart,
      candidate.main,
      candidate.thumbnail,
      candidate.original,
      candidate.url,
      candidate.path,
      candidate.filename,
      candidate.name,
    ];

    for (const value of objectCandidates) {
      const extracted =
        extractFromImageCandidate(value);

      if (extracted) {
        return extracted;
      }
    }
  }

  return null;
};

/* =========================================================
   DISPLAY IMAGE URL
========================================================= */

/**
 * Converts the cart image into a browser-ready URL.
 *
 * IMPORTANT:
 *
 * This function is ONLY for displaying the image.
 *
 * It must NOT be used when sending the image to save-order.
 */

const getImageUrl = (item) => {
  let img = extractImageValue(item);

  if (!img) {
    return null;
  }

  img = img.trim();

  /* -------------------------------------------------------
     FULL HTTP / HTTPS URL
  ------------------------------------------------------- */

  if (/^https?:\/\//i.test(img)) {
    return img;
  }

  /* -------------------------------------------------------
     DATA / BLOB URL
  ------------------------------------------------------- */

  if (
    img.startsWith('data:') ||
    img.startsWith('blob:')
  ) {
    return img;
  }

  /* -------------------------------------------------------
     REMOVE LEADING SLASHES
  ------------------------------------------------------- */

  img = img.replace(/^\/+/, '');

  /* -------------------------------------------------------
     ALREADY HAS PATH
  ------------------------------------------------------- */

  if (
    img.startsWith('uploads/') ||
    img.startsWith('images/') ||
    img.startsWith('static/') ||
    img.startsWith('media/')
  ) {
    return `${API_BASE}/${img}`;
  }

  /* -------------------------------------------------------
     DEFAULT UPLOAD PATH
  ------------------------------------------------------- */

  return `${API_BASE}/uploads/${img}`;
};

/* =========================================================
   ORIGINAL IMAGE VALUE FOR ORDER API
========================================================= */

/**
 * IMPORTANT:
 *
 * This function returns the ORIGINAL image value.
 *
 * DO NOT send getImageUrl(item) to save-order.
 */

const getOrderImageValue = (item) => {
  if (!item) {
    return null;
  }

  /* -------------------------------------------------------
     Prefer item.image
  ------------------------------------------------------- */

  if (item?.image) {
    return item.image;
  }

  /* -------------------------------------------------------
     Then item.photo
  ------------------------------------------------------- */

  if (item?.photo) {
    return item.photo;
  }

  /* -------------------------------------------------------
     Then product.image
  ------------------------------------------------------- */

  if (item?.product?.image) {
    return item.product.image;
  }

  /* -------------------------------------------------------
     Then item.images
  ------------------------------------------------------- */

  if (item?.images) {
    return Array.isArray(item.images)
      ? item.images[0] || null
      : item.images;
  }

  /* -------------------------------------------------------
     Then product.images
  ------------------------------------------------------- */

  if (item?.product?.images) {
    return Array.isArray(item.product.images)
      ? item.product.images[0] || null
      : item.product.images;
  }

  return null;
};

/* =========================================================
   ORDER SUMMARY PAGE
========================================================= */

const OrderSummaryPage = () => {
  const { cart } = useCart();

  const [userInfo, setUserInfo] = useState(null);

  const [loading, setLoading] = useState(true);

  const [placingOrder, setPlacingOrder] = useState(false);

  const [error, setError] = useState(null);

  /* =======================================================
     SAFE CART
  ======================================================= */

  const safeCart = Array.isArray(cart)
    ? cart
    : [];

  /* =======================================================
     SUBTOTAL
  ======================================================= */

  const subtotal = useMemo(() => {
    return safeCart.reduce((sum, item) => {
      const price =
        Number(item?.price) || 0;

      const quantity =
        Number(item?.quantity) || 0;

      return sum + price * quantity;
    }, 0);
  }, [safeCart]);

  /* =======================================================
     SHIPPING
  ======================================================= */

  /*
    FIXED SHIPPING:

    Empty cart:
      £0.00

    Cart contains products:
      £5.99
  */

  const shipping = useMemo(() => {
    if (safeCart.length === 0) {
      return 0;
    }

    return SHIPPING_COST;
  }, [safeCart]);

  /* =======================================================
     TOTAL
  ======================================================= */

  /*
    TOTAL = SUBTOTAL + SHIPPING
  */

  const total = useMemo(() => {
    return subtotal + shipping;
  }, [subtotal, shipping]);

  /* =======================================================
     FETCH USER
  ======================================================= */

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        setLoading(true);

        setError(null);

        const { data } = await axios.get(
          `${API_BASE}/api/users/profile`,
          {
            withCredentials: true,
          }
        );

        setUserInfo(data);
      } catch (err) {
        console.error(
          'PROFILE ERROR:',
          err
        );

        console.error(
          'PROFILE RESPONSE:',
          err.response?.data
        );

        setError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            'Failed to fetch your user details.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
  }, []);

  /* =======================================================
     PLACE ORDER
  ======================================================= */

  const handlePlaceOrder = async () => {
    /* -----------------------------------------------------
       EMPTY CART
    ----------------------------------------------------- */

    if (!safeCart || safeCart.length === 0) {
      setError('Your cart is empty.');
      return;
    }

    /* -----------------------------------------------------
       PREVENT DOUBLE CLICK
    ----------------------------------------------------- */

    if (placingOrder) {
      return;
    }

    try {
      setPlacingOrder(true);

      setError(null);

      /* ---------------------------------------------------
         RECALCULATE TOTALS BEFORE CHECKOUT
         --------------------------------------------------- */

      const calculatedSubtotal =
        safeCart.reduce((sum, item) => {
          const price =
            Number(item?.price) || 0;

          const quantity =
            Number(item?.quantity) || 0;

          return sum + price * quantity;
        }, 0);

      const calculatedShipping =
        SHIPPING_COST;

      const calculatedTotal =
        calculatedSubtotal +
        calculatedShipping;

      console.log(
        'CHECKOUT TOTAL CALCULATION:',
        {
          subtotal: calculatedSubtotal,
          shipping: calculatedShipping,
          total: calculatedTotal,
        }
      );

      /* ---------------------------------------------------
         CREATE PRODUCTS PAYLOAD
      --------------------------------------------------- */

      const products = safeCart.map((item) => {
        const orderImage =
          getOrderImageValue(item);

        const displayImage =
          getImageUrl(item);

        /*
          DEBUGGING
        */

        console.log(
          'ORDER PRODUCT:',
          {
            id:
              item?._id ||
              item?.id,

            name:
              item?.name,

            price:
              item?.price,

            quantity:
              item?.quantity,

            originalImage:
              item?.image,

            photo:
              item?.photo,

            images:
              item?.images,

            productImage:
              item?.product?.image,

            productImages:
              item?.product?.images,

            orderImage,

            displayImage,
          }
        );

        return {
          name:
            item?.name || '',

          price:
            Number(item?.price) || 0,

          quantity:
            Number(item?.quantity) || 1,

          description:
            item?.description || '',

          /*
            IMPORTANT:

            Send original image value.

            NOT displayImage.
          */

          image:
            orderImage,
        };
      });

      /* ---------------------------------------------------
         ORDER DATA
      --------------------------------------------------- */

      const orderData = {
        products,

        /*
          CORRECT SUBTOTAL
        */

        subtotal:
          calculatedSubtotal,

        /*
          FIXED SHIPPING £5.99
        */

        shipping:
          calculatedShipping,

        /*
          SUBTOTAL + SHIPPING
        */

        total:
          calculatedTotal,

        shippingAddress:
          userInfo?.shippingAddress ||
          null,
      };

      console.log(
        'SENDING ORDER DATA:',
        orderData
      );

      /* ---------------------------------------------------
         SAVE ORDER
      --------------------------------------------------- */

      const { data } =
        await axios.post(
          `${API_BASE}/api/orders/save-order`,
          orderData,
          {
            withCredentials: true,
          }
        );

      console.log(
        'ORDER SAVED:',
        data
      );

      /* ---------------------------------------------------
         VALIDATE ORDER ID
      --------------------------------------------------- */

      if (!data?._id) {
        throw new Error(
          'Order was created but no order ID was returned.'
        );
      }

      /* ---------------------------------------------------
         STRIPE CHECKOUT
      --------------------------------------------------- */

      console.log(
        'CREATING STRIPE CHECKOUT:',
        {
          orderId:
            data._id,

          cart:
            safeCart,

          subtotal:
            calculatedSubtotal,

          shipping:
            calculatedShipping,

          total:
            calculatedTotal,

          user: {
            id:
              userInfo?._id,

            email:
              userInfo?.email,
          },

          shippingAddress:
            userInfo?.shippingAddress ||
            null,
        }
      );

      const checkoutSession =
        await axios.post(
          `${API_BASE}/api/stripe/create-checkout-session`,
          {
            cart:
              safeCart,

            orderId:
              data._id,

            /*
              SEND THE CALCULATED VALUES
            */

            subtotal:
              calculatedSubtotal,

            shipping:
              calculatedShipping,

            total:
              calculatedTotal,

            user: {
              id:
                userInfo?._id,

              email:
                userInfo?.email,
            },

            shippingAddress:
              userInfo?.shippingAddress ||
              null,
          },
          {
            withCredentials: true,
          }
        );

      console.log(
        'STRIPE CHECKOUT RESPONSE:',
        checkoutSession.data
      );

      /* ---------------------------------------------------
         STRIPE URL
      --------------------------------------------------- */

      if (
        checkoutSession?.data?.url
      ) {
        window.location.href =
          checkoutSession.data.url;

        return;
      }

      throw new Error(
        'Stripe checkout URL was not returned.'
      );

    } catch (err) {
      console.error(
        'PLACE ORDER ERROR:',
        err
      );

      console.error(
        'RESPONSE DATA:',
        err.response?.data
      );

      console.error(
        'RESPONSE STATUS:',
        err.response?.status
      );

      console.error(
        'RESPONSE HEADERS:',
        err.response?.headers
      );

      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Failed to place order.'
      );

      setPlacingOrder(false);
    }
  };

  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {
    return (
      <div style={styles.center}>
        <div style={styles.loadingCard}>
          <Loader2
            size={42}
            style={styles.spinner}
          />

          <p style={styles.loadingText}>
            Loading your order summary...
          </p>
        </div>
      </div>
    );
  }

  /* =======================================================
     USER FETCH ERROR
  ======================================================= */

  if (error && !userInfo) {
    return (
      <div style={styles.center}>
        <div style={styles.errorCard}>
          <XCircle
            size={48}
            color="#dc3545"
          />

          <h3 style={styles.errorTitle}>
            Something went wrong
          </h3>

          <p style={styles.errorText}>
            {error}
          </p>

          <button
            style={styles.retryButton}
            onClick={() =>
              window.location.reload()
            }
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  /* =======================================================
     EMPTY CART
  ======================================================= */

  if (!safeCart || safeCart.length === 0) {
    return (
      <div style={styles.center}>
        <div style={styles.emptyCard}>
          <Package
            size={55}
            strokeWidth={1.5}
          />

          <h2>
            Your cart is empty
          </h2>

          <p>
            Add some products before
            proceeding to checkout.
          </p>

          <button
            style={styles.retryButton}
            onClick={() => {
              window.location.href =
                '/shop';
            }}
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  /* =======================================================
     PAGE
  ======================================================= */

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* =================================================
            HEADER
        ================================================= */}

        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>
              Order Summary
            </h1>

            <p style={styles.subtitle}>
              Review your order before payment
            </p>
          </div>
        </div>

        {/* =================================================
            ERROR ALERT
        ================================================= */}

        {error && (
          <div style={styles.alert}>
            <AlertCircle size={20} />

            <span>
              {error}
            </span>

            <button
              onClick={() =>
                setError(null)
              }
              style={styles.alertClose}
            >
              ×
            </button>
          </div>
        )}

        {/* =================================================
            CUSTOMER INFORMATION
        ================================================= */}

        {userInfo && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <div style={styles.sectionIcon}>
                <User size={20} />
              </div>

              <div>
                <h2 style={styles.sectionTitle}>
                  Customer Information
                </h2>

                <p style={styles.sectionSubtitle}>
                  Your account details
                </p>
              </div>
            </div>

            <div style={styles.customerGrid}>

              {/* CUSTOMER */}

              <div style={styles.infoCard}>
                <div style={styles.infoIcon}>
                  <User size={18} />
                </div>

                <div>
                  <span style={styles.infoLabel}>
                    Customer
                  </span>

                  <p style={styles.infoValue}>
                    {userInfo?.name ||
                      userInfo?.email ||
                      'Customer'}
                  </p>

                  {userInfo?.email && (
                    <p style={styles.infoSecondary}>
                      {userInfo.email}
                    </p>
                  )}
                </div>
              </div>

              {/* SHIPPING */}

              <div style={styles.infoCard}>
                <div style={styles.infoIcon}>
                  <MapPin size={18} />
                </div>

                <div>
                  <span style={styles.infoLabel}>
                    Shipping Address
                  </span>

                  {userInfo?.shippingAddress ? (
                    <p style={styles.address}>
                      {userInfo.shippingAddress.address}
                      <br />

                      {userInfo.shippingAddress.city}

                      {userInfo.shippingAddress.city &&
                        userInfo.shippingAddress.postcode
                        ? ', '
                        : ''}

                      {userInfo.shippingAddress.postcode}

                      <br />

                      {userInfo.shippingAddress.country}
                    </p>
                  ) : (
                    <p style={styles.noAddress}>
                      No shipping address saved
                    </p>
                  )}
                </div>
              </div>

            </div>
          </div>
        )}

        {/* =================================================
            PRODUCTS
        ================================================= */}

        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionIcon}>
              <Package size={20} />
            </div>

            <div>
              <h2 style={styles.sectionTitle}>
                Your Products
              </h2>

              <p style={styles.sectionSubtitle}>
                {safeCart.length}{' '}
                {safeCart.length === 1
                  ? 'product'
                  : 'products'}{' '}
                in your order
              </p>
            </div>
          </div>

          <div style={styles.products}>
            {safeCart.map((item, index) => (
              <ProductRow
                key={
                  item?._id ||
                  item?.id ||
                  `${item?.name}-${index}`
                }
                item={item}
                imageUrl={
                  getImageUrl(item)
                }
              />
            ))}
          </div>
        </div>

        {/* =================================================
            ORDER TOTALS
        ================================================= */}

        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionIcon}>
              <CreditCard size={20} />
            </div>

            <div>
              <h2 style={styles.sectionTitle}>
                Payment Summary
              </h2>

              <p style={styles.sectionSubtitle}>
                Your order total
              </p>
            </div>
          </div>

          <div style={styles.summary}>

            {/* SUBTOTAL */}

            <SummaryRow
              label="Subtotal"
              value={subtotal}
            />

            {/* SHIPPING */}

            <SummaryRow
              label="Shipping"
              value={shipping}
            />

            {/* SHIPPING INFORMATION */}

            <div style={styles.shippingInfo}>
              <Truck size={15} />

              <span>
                Standard delivery: £
                {shipping.toFixed(2)}
              </span>
            </div>

            {/* TOTAL */}

            <div style={styles.totalRow}>
              <span>
                Total
              </span>

              <span>
                £{total.toFixed(2)}
              </span>
            </div>

          </div>
        </div>

        {/* =================================================
            CHECKOUT
        ================================================= */}

        <div style={styles.checkoutSection}>

          <div style={styles.secureText}>
            <CheckCircle size={17} />

            <span>
              Secure checkout powered by Stripe
            </span>
          </div>

          <button
            style={{
              ...styles.button,

              ...(placingOrder
                ? styles.buttonDisabled
                : {}),
            }}
            onClick={handlePlaceOrder}
            disabled={placingOrder}
          >
            {placingOrder ? (
              <>
                <Loader2
                  size={20}
                  style={
                    styles.buttonSpinner
                  }
                />

                Processing Order...
              </>
            ) : (
              <>
                <CheckCircle size={20} />

                Pay £{total.toFixed(2)}
              </>
            )}
          </button>

          <p style={styles.paymentNote}>
            You will be securely redirected to
            Stripe to complete your payment.
          </p>

        </div>

      </div>
    </div>
  );
};

/* =========================================================
   PRODUCT ROW
========================================================= */

const ProductRow = ({
  item,
  imageUrl,
}) => {
  const [imageError, setImageError] =
    useState(false);

  const price =
    Number(item?.price) || 0;

  const quantity =
    Number(item?.quantity) || 1;

  const lineTotal =
    price * quantity;

  return (
    <div style={styles.productRow}>

      {/* IMAGE + PRODUCT */}

      <div style={styles.productLeft}>

        <div style={styles.imageWrapper}>
          {imageUrl && !imageError ? (
            <img
              src={imageUrl}
              alt={
                item?.name ||
                'Product'
              }
              style={styles.image}
              loading="lazy"
              onError={(e) => {
                console.error(
                  'ORDER IMAGE FAILED:',
                  {
                    name:
                      item?.name,

                    imageUrl,

                    item,
                  }
                );

                setImageError(true);
              }}
            />
          ) : (
            <div
              style={
                styles.imagePlaceholder
              }
            >
              <Package
                size={25}
                color="#aaa"
              />
            </div>
          )}
        </div>

        <div style={styles.productInfo}>

          <p style={styles.productName}>
            {item?.name ||
              'Unnamed Product'}
          </p>

          {item?.brand && (
            <p style={styles.productBrand}>
              {item.brand}
            </p>
          )}

          <p style={styles.quantity}>
            Quantity: {quantity}
          </p>

        </div>
      </div>

      {/* PRICE */}

      <div style={styles.priceContainer}>

        <span style={styles.unitPrice}>
          £{price.toFixed(2)}
        </span>

        {quantity > 1 && (
          <span style={styles.lineTotal}>
            £{lineTotal.toFixed(2)}
          </span>
        )}

      </div>
    </div>
  );
};

/* =========================================================
   SUMMARY ROW
========================================================= */

const SummaryRow = ({
  label,
  value,
}) => {
  return (
    <div style={styles.row}>

      <span style={styles.rowLabel}>
        {label}
      </span>

      <span style={styles.rowValue}>
        £{Number(value).toFixed(2)}
      </span>

    </div>
  );
};

/* =========================================================
   STYLES
========================================================= */

const styles = {

  /* -------------------------------------------------------
     PAGE
  ------------------------------------------------------- */

  page: {
    minHeight: '100vh',
    background: '#f7f8fa',
    padding: '40px 20px 70px',
    boxSizing: 'border-box',
  },

  container: {
    width: '100%',
    maxWidth: '850px',
    margin: '0 auto',
  },

  /* -------------------------------------------------------
     HEADER
  ------------------------------------------------------- */

  header: {
    marginBottom: 25,
  },

  title: {
    margin: 0,
    fontSize: 32,
    fontWeight: 800,
    color: '#111827',
    letterSpacing: '-0.5px',
  },

  subtitle: {
    margin: '7px 0 0',
    color: '#6b7280',
    fontSize: 15,
  },

  /* -------------------------------------------------------
     SECTION
  ------------------------------------------------------- */

  section: {
    background: '#fff',
    border: '1px solid #e8eaed',
    borderRadius: 16,
    padding: 24,
    marginBottom: 18,
    boxShadow:
      '0 3px 15px rgba(0,0,0,0.035)',
  },

  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 20,
  },

  sectionIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: '#eef4ff',
    color: '#4f8cff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  sectionTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: 700,
    color: '#111827',
  },

  sectionSubtitle: {
    margin: '3px 0 0',
    color: '#8a8f98',
    fontSize: 13,
  },

  /* -------------------------------------------------------
     CUSTOMER
  ------------------------------------------------------- */

  customerGrid: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 15,
  },

  infoCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: 16,
    background: '#fafafa',
    borderRadius: 12,
    border: '1px solid #eee',
  },

  infoIcon: {
    width: 35,
    height: 35,
    borderRadius: 9,
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#4f8cff',
    flexShrink: 0,
  },

  infoLabel: {
    display: 'block',
    color: '#8a8f98',
    fontSize: 12,
    marginBottom: 4,
  },

  infoValue: {
    margin: 0,
    fontSize: 15,
    fontWeight: 600,
    color: '#222',
  },

  infoSecondary: {
    margin: '3px 0 0',
    color: '#777',
    fontSize: 13,
  },

  address: {
    margin: 0,
    color: '#333',
    fontSize: 14,
    lineHeight: 1.55,
  },

  noAddress: {
    margin: 0,
    color: '#dc3545',
    fontSize: 14,
  },

  /* -------------------------------------------------------
     PRODUCTS
  ------------------------------------------------------- */

  products: {
    display: 'flex',
    flexDirection: 'column',
  },

  productRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 15,
    padding: '14px 0',
    borderBottom:
      '1px solid #f0f0f0',
  },

  productLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    minWidth: 0,
  },

  imageWrapper: {
    width: 72,
    height: 72,
    borderRadius: 12,
    overflow: 'hidden',
    flexShrink: 0,
    background: '#f3f4f6',
  },

  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },

  imagePlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f3f4f6',
  },

  productInfo: {
    minWidth: 0,
  },

  productName: {
    margin: 0,
    fontWeight: 600,
    color: '#1f2937',
    fontSize: 15,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '500px',
  },

  productBrand: {
    margin: '3px 0 0',
    color: '#777',
    fontSize: 12,
  },

  quantity: {
    margin: '5px 0 0',
    color: '#777',
    fontSize: 13,
  },

  priceContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    flexShrink: 0,
  },

  unitPrice: {
    fontWeight: 600,
    color: '#222',
    fontSize: 15,
  },

  lineTotal: {
    marginTop: 3,
    color: '#777',
    fontSize: 12,
  },

  /* -------------------------------------------------------
     SUMMARY
  ------------------------------------------------------- */

  summary: {
    paddingTop: 2,
  },

  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
    fontSize: 15,
  },

  rowLabel: {
    color: '#666',
  },

  rowValue: {
    color: '#222',
    fontWeight: 500,
  },

  shippingInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    marginTop: 8,
    marginBottom: 12,
    padding: '9px 12px',
    background: '#f0f7ff',
    borderRadius: 8,
    color: '#2563eb',
    fontSize: 12,
  },

  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTop:
      '1px solid #dedede',
    marginTop: 15,
    paddingTop: 17,
    fontSize: 21,
    fontWeight: 800,
    color: '#111827',
  },

  /* -------------------------------------------------------
     CHECKOUT
  ------------------------------------------------------- */

  checkoutSection: {
    background: '#fff',
    borderRadius: 16,
    border: '1px solid #e8eaed',
    padding: 24,
    boxShadow:
      '0 3px 15px rgba(0,0,0,0.035)',
  },

  secureText: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 7,
    color: '#16803c',
    fontSize: 13,
    marginBottom: 15,
  },

  button: {
    width: '100%',
    minHeight: 54,
    border: 'none',
    borderRadius: 12,
    background: '#4f8cff',
    color: '#fff',
    fontWeight: 700,
    fontSize: 16,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 9,
    transition:
      'all 0.2s ease',
  },

  buttonDisabled: {
    opacity: 0.65,
    cursor: 'not-allowed',
  },

  buttonSpinner: {
    animation:
      'spin 1s linear infinite',
  },

  paymentNote: {
    textAlign: 'center',
    color: '#888',
    fontSize: 12,
    margin: '12px 0 0',
  },

  /* -------------------------------------------------------
     ALERT
  ------------------------------------------------------- */

  alert: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#fff1f2',
    color: '#b42318',
    border:
      '1px solid #fecdd3',
    borderRadius: 10,
    padding: '12px 14px',
    marginBottom: 18,
    fontSize: 14,
  },

  alertClose: {
    marginLeft: 'auto',
    border: 'none',
    background: 'transparent',
    color: '#b42318',
    fontSize: 22,
    cursor: 'pointer',
    lineHeight: 1,
  },

  /* -------------------------------------------------------
     LOADING
  ------------------------------------------------------- */

  center: {
    minHeight: '100vh',
    background: '#f7f8fa',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    boxSizing: 'border-box',
  },

  loadingCard: {
    background: '#fff',
    borderRadius: 16,
    padding: 35,
    textAlign: 'center',
    border:
      '1px solid #eee',
  },

  spinner: {
    animation:
      'spin 1s linear infinite',
    color: '#4f8cff',
  },

  loadingText: {
    margin: '15px 0 0',
    color: '#666',
  },

  /* -------------------------------------------------------
     ERROR
  ------------------------------------------------------- */

  errorCard: {
    width: '100%',
    maxWidth: 430,
    background: '#fff',
    borderRadius: 16,
    padding: 35,
    textAlign: 'center',
    border:
      '1px solid #eee',
    boxShadow:
      '0 10px 30px rgba(0,0,0,0.05)',
  },

  errorTitle: {
    margin: '15px 0 8px',
    color: '#222',
  },

  errorText: {
    color: '#dc3545',
    margin: 0,
    lineHeight: 1.5,
  },

  retryButton: {
    marginTop: 20,
    padding: '11px 20px',
    border: 'none',
    borderRadius: 9,
    background: '#4f8cff',
    color: '#fff',
    fontWeight: 600,
    cursor: 'pointer',
  },

  /* -------------------------------------------------------
     EMPTY
  ------------------------------------------------------- */

  emptyCard: {
    width: '100%',
    maxWidth: 430,
    background: '#fff',
    borderRadius: 16,
    padding: 40,
    textAlign: 'center',
    border:
      '1px solid #eee',
    color: '#666',
  },
};

/* =========================================================
   GLOBAL SPINNER ANIMATION
========================================================= */

if (
  typeof document !== 'undefined' &&
  !document.getElementById(
    'order-summary-spinner-style'
  )
) {
  const style =
    document.createElement('style');

  style.id =
    'order-summary-spinner-style';

  style.innerHTML = `
    @keyframes spin {
      from {
        transform: rotate(0deg);
      }

      to {
        transform: rotate(360deg);
      }
    }
  `;

  document.head.appendChild(style);
}

export default OrderSummaryPage;

